import { Button, DataTable, StatusBadge } from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { useMemo } from 'react';
import type { EndpointHealth } from '../../schemas/webhook.schema';

export function EndpointsTable({
  rows,
  loading,
  onSelectEndpoint,
}: {
  rows: EndpointHealth[];
  loading: boolean;
  onSelectEndpoint: (e: EndpointHealth) => void;
}) {
  const columns = useMemo<ColumnDef<EndpointHealth>[]>(
    () => [
      { accessorKey: 'appName', header: 'App' },
      {
        accessorKey: 'name',
        header: 'Webhook',
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <Button
              variant="link"
              className="h-auto p-0"
              onClick={() => onSelectEndpoint(row.original)}
            >
              {row.original.name}
            </Button>
            {row.original.disabledAt ? <StatusBadge tone="neutral">disabled</StatusBadge> : null}
          </span>
        ),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        cell: ({ row }) => <code className="font-mono text-xs">{row.original.url}</code>,
      },
      {
        id: 'counts',
        header: 'In window',
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {row.original.delivered} ok ·{' '}
            <span className={row.original.failed > 0 ? 'text-destructive' : undefined}>
              {row.original.failed} failed
            </span>
          </span>
        ),
      },
      {
        id: 'last',
        header: 'Last delivery',
        cell: ({ row }) => {
          const e = row.original;
          if (!e.lastStatus) return <span className="text-xs text-muted-foreground">never</span>;
          return (
            <span className="flex items-center gap-2">
              <StatusBadge tone={e.lastStatus === 'DELIVERED' ? 'success' : 'danger'}>
                {e.lastStatus === 'DELIVERED' ? 'delivered' : 'failed'}
              </StatusBadge>
              <span className="text-xs text-muted-foreground">
                {e.lastError ? `${e.lastError} · ` : ''}
                {e.lastRequestedAt ? new Date(e.lastRequestedAt).toLocaleString() : ''}
              </span>
            </span>
          );
        },
      },
    ],
    [onSelectEndpoint],
  );
  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={loading}
      hidePagination
      aria-label="Webhook endpoints"
      empty={
        <p className="py-6 text-center text-sm text-muted-foreground">
          No webhooks declared: add fisto.Webhook(app, name, …) to a product’s cac/stack.ts
        </p>
      }
    />
  );
}
